(function () {
  const TAG_CONTAINER_SELECTOR = ".product_tags___";

  // Keep your existing selector, but we won't rely on it alone anymore
  const PRODUCT_CARD_SELECTOR =
    "[data-product-handle], .product-card, .card, .grid__item, [data-product-card]";

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (m) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    }[m]));
  }

  function parseHandleFromProductUrl(url) {
    if (!url) return "";
    const idx = url.indexOf("/products/");
    if (idx === -1) return "";
    return url
      .slice(idx + 10)
      .split("?")[0]
      .split("#")[0]
      .split("/")[0];
  }

  function getHandleFromCard(card) {
    if (!card) return "";
    const h = card.getAttribute("data-product-handle");
    if (h) return h;

    const link = card.querySelector('a[href*="/products/"]')?.getAttribute("href") || "";
    return parseHandleFromProductUrl(link);
  }

  // PDP fallback: get handle from current URL (/products/handle)
  function getHandleFromPage() {
    const m = window.location.pathname.match(/\/products\/([^\/]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  }

  /**
   * ✅ Robust handle finder:
   * Works for collection cards, sliders, and PDP "recommended products" tiles,
   * even when the wrapper doesn't match PRODUCT_CARD_SELECTOR.
   */
  function getHandleForContainer(container) {
    if (!container) return "";

    // 1) If container itself has it
    const selfHandle = container.getAttribute("data-product-handle");
    if (selfHandle) return selfHandle;

    // 2) Closest ancestor with data-product-handle
    const byData = container.closest("[data-product-handle]");
    if (byData) {
      const h = byData.getAttribute("data-product-handle");
      if (h) return h;
    }

    // 3) Closest known product card wrapper (your original logic)
    const card = container.closest(PRODUCT_CARD_SELECTOR);
    const fromCard = getHandleFromCard(card);
    if (fromCard) return fromCard;

    // 4) Closest anchor that links to a product
    const closestLink = container.closest('a[href*="/products/"]');
    const fromClosestLink = parseHandleFromProductUrl(closestLink?.getAttribute("href"));
    if (fromClosestLink) return fromClosestLink;

    // 5) Any product link inside the container or nearby parent
    const innerLink =
      container.querySelector('a[href*="/products/"]') ||
      container.parentElement?.querySelector('a[href*="/products/"]');
    const fromInnerLink = parseHandleFromProductUrl(innerLink?.getAttribute("href"));
    if (fromInnerLink) return fromInnerLink;

    // 6) Last resort: PDP product handle (ONLY if nothing else exists)
    return getHandleFromPage();
  }

  async function fetchTags(handle) {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("fetch failed");
    const data = await res.json();
    return Array.isArray(data?.tags) ? data.tags : [];
  }

  function makePill(tagText, isButton) {
    const el = document.createElement(isButton ? "button" : "span");
    el.className =
      "product_tag-pill" +
      (isButton ? " product_tag-pill--btn product_tag-more" : "");
    if (isButton) el.type = "button";
    el.innerHTML = `<span class="product_tag-dot"><svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.95 3.9C3.02696 3.9 3.9 3.02696 3.9 1.95C3.9 0.873045 3.02696 0 1.95 0C0.873045 0 0 0.873045 0 1.95C0 3.02696 0.873045 3.9 1.95 3.9Z" fill="black"/></svg></span><span class="product_tag-text">${escapeHtml(
      tagText
    )}</span>`;
    return el;
  }

  function makeHiddenTag(tagText) {
    const el = document.createElement("span");
    el.className = "product_tag-pill product_tag-reveal is-hidden";
    el.innerHTML = `<span class="product_tag-dot"><svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.95 3.9C3.02696 3.9 3.9 3.02696 3.9 1.95C3.9 0.873045 3.02696 0 1.95 0C0.873045 0 0 0.873045 0 1.95C0 3.02696 0.873045 3.9 1.95 3.9Z" fill="black"/></svg></span><span class="product_tag-text">${escapeHtml(
      tagText
    )}</span>`;
    return el;
  }

  function renderTagsUI(container, tags) {
    container.innerHTML = "";
    if (!tags.length) {
      container.style.display = "none";
      return;
    }
    container.style.display = "";

    const firstTag = tags[0];
    const remaining = tags.slice(1);

    container.appendChild(makePill(firstTag, false));

    if (!remaining.length) return;

    const btn = makePill(`+ ${remaining.length} Smaker`, true);
    btn.setAttribute("aria-expanded", "false");
    container.appendChild(btn);

    remaining.forEach((t) => container.appendChild(makeHiddenTag(t)));

    function open() {
      btn.setAttribute("aria-expanded", "true");
      btn.classList.add("is-hidden-btn");
      container
        .querySelectorAll(".product_tag-reveal.is-hidden")
        .forEach((el) => el.classList.remove("is-hidden"));
    }

    btn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      open();
    });
  }

  // ✅ One global outside-click handler (instead of adding one per container)
  document.addEventListener("click", function (e) {
    document.querySelectorAll(TAG_CONTAINER_SELECTOR).forEach((container) => {
      if (!container.contains(e.target)) {
        const btn = container.querySelector(".product_tag-more");
        if (!btn) return;

        btn.setAttribute("aria-expanded", "false");
        btn.classList.remove("is-hidden-btn");
        container
          .querySelectorAll(".product_tag-reveal")
          .forEach((el) => el.classList.add("is-hidden"));
      }
    });
  });

  async function initContainer(container) {
    if (!container || container.dataset.tagsInit === "1") return;
    container.dataset.tagsInit = "1";

    const handle = getHandleForContainer(container);
    if (!handle) return;

    try {
      const tags = await fetchTags(handle);
      renderTagsUI(container, tags);
    } catch (e) {
      // If fetch fails, allow retry later
      container.dataset.tagsInit = "0";
    }
  }

  function initAll(root = document) {
    root.querySelectorAll(TAG_CONTAINER_SELECTOR).forEach((c) => initContainer(c));
  }

  document.addEventListener("DOMContentLoaded", function () {
    initAll();

    // ✅ Watches for PDP product recommendations injected after load
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;

          // If a tag container itself was injected
          if (node.matches?.(TAG_CONTAINER_SELECTOR)) initContainer(node);

          // Or if injected subtree contains tag containers
          const found = node.querySelectorAll?.(TAG_CONTAINER_SELECTOR);
          if (found && found.length) initAll(node);
        }
      }
    });

    obs.observe(document.body, { childList: true, subtree: true });
  });

  // Optional: if theme re-renders product sections (safe to keep)
  document.addEventListener("shopify:section:load", () => initAll());








if (document.querySelector('.template-collection')) {

  function markLastInRow() {
    const list = document.querySelector('.product-grid');
    if (!list) return;

    const items = Array.from(list.querySelectorAll('li'));
    if (!items.length) return;

    // reset classes
    items.forEach(li => li.classList.remove('last-in-row'));

    const rows = new Map();
    const EPS = 2;

    items.forEach(item => {
      const top = Math.round(item.offsetTop);

      // find matching row (with tolerance)
      let matchedRow = null;
      for (let key of rows.keys()) {
        if (Math.abs(key - top) <= EPS) {
          matchedRow = key;
          break;
        }
      }

      if (matchedRow === null) {
        rows.set(top, [item]);
      } else {
        rows.get(matchedRow).push(item);
      }
    });

    // mark last item in each row
    rows.forEach(rowItems => {
      const last = rowItems[rowItems.length - 1];
      if (last) last.classList.add('last-in-row');
    });
  }

  // run on load
  window.addEventListener('load', markLastInRow);

  // run after images load
  document.querySelectorAll('.product-grid img').forEach(img => {
    img.addEventListener('load', markLastInRow);
  });

  // debounce resize
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(markLastInRow, 100);
  });

}





/**** ADD TO CARD Button celerbations style*****/
document.addEventListener('click', function (e) {
  const button = e.target.closest(
    'add-to-cart-component button[type="submit"]'
  );
  if (!button) return;
  if (!window.themeConfetti?.enabled) return;

  const styles = {
    classic: { particleCount: 100, spread: 70 },
    subtle: { particleCount: 50, spread: 45 },
    explosive: { particleCount: 140, spread: 90 }
  };

  const base = styles[window.themeConfetti.style] || styles.classic;
  const colors = ['#12120F', '#E63946', '#F1FAEE'];

  switch (window.themeConfetti.layout) {

    // 1️⃣ Left & Right (current)
    case 'sides':
      confetti({ ...base, angle: 60, origin: { x: 0, y: 0.6 }, colors });
      confetti({ ...base, angle: 120, origin: { x: 1, y: 0.6 }, colors });
      break;

    // 2️⃣ Top rain
    case 'top':
      confetti({
        ...base,
        angle: 90,
        spread: 120,
        origin: { x: 0.5, y: 0 },
        gravity: 0.8,
        colors
      });
      break;

    // 3️⃣ Center burst
    case 'center':
      confetti({
        ...base,
        angle: 90,
        spread: 360,
        origin: { x: 0.5, y: 0.5 },
        colors
      });
      break;

    // 4️⃣ Four corners
    case 'corners':
      confetti({ ...base, angle: 45, origin: { x: 0, y: 0 }, colors });
      confetti({ ...base, angle: 135, origin: { x: 1, y: 0 }, colors });
      confetti({ ...base, angle: 315, origin: { x: 0, y: 1 }, colors });
      confetti({ ...base, angle: 225, origin: { x: 1, y: 1 }, colors });
      break;

    // 5️⃣ Diagonal sweep
    case 'diagonal':
      confetti({
        ...base,
        angle: 45,
        spread: 80,
        origin: { x: 0, y: 1 },
        colors
      });
      break;

    // 6️⃣ Minimal spark (premium)
    case 'minimal':
      confetti({
        particleCount: 25,
        spread: 30,
        angle: 90,
        origin: { x: 0.5, y: 0.7 },
        colors
      });
      break;

    default:
      confetti({ ...base, origin: { x: 0.5, y: 0.5 }, colors });
  }
});






 const selectors = [
    '#shopify-section-template--28126114054485__collection_list_HTNEp4 .resource-list__carousel',
    '#shopify-section-template--28126114054485__section_BUXmUz .border-style',
    '#shopify-section-template--28126114054485__section_P4M3jd'
  ];

  function setZIndex(isOpen) {
    selectors.forEach((selector) => {
      const el = document.querySelector(selector);
      if (!el) return;

      if (isOpen) {
        el.style.zIndex = '-1';
      } else {
        el.style.zIndex = '';
      }
    });

    console.log('Menu state →', isOpen ? 'OPEN' : 'CLOSED');
  }

  function initObserver() {
    const target = document.querySelector('.menu-drawer-container');
    if (!target) return false;

    function checkState() {
      const isOpen = target.classList.contains('menu-open');
      setZIndex(isOpen);
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          checkState();
        }
      });
    });

    observer.observe(target, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Run once initially
    checkState();

    return true;
  }

  // Handle dynamic menu load (Horizon safe)
  if (!initObserver()) {
    const bodyObserver = new MutationObserver(() => {
      if (initObserver()) bodyObserver.disconnect();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }


})();













document.addEventListener('DOMContentLoaded', function () {
  const items = document.querySelectorAll('.multiLang ul.LangTranslte li');
  if (!items.length) return;

  const SOURCE_LANG = 'sv'; // your default language

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    document.cookie = `${name}=${value};${expires};path=/`;
  }

  function setCookieOnRootDomain(name, value, days) {
    // set cookie on current domain + root domain (best chance to work)
    setCookie(name, value, days);

    const hostParts = location.hostname.split('.');
    if (hostParts.length >= 2) {
      const rootDomain = '.' + hostParts.slice(-2).join('.');
      const d = new Date();
      d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = "expires=" + d.toUTCString();
      document.cookie = `${name}=${value};${expires};path=/;domain=${rootDomain}`;
    }
  }

  function deleteCookieOnRootDomain(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

    const hostParts = location.hostname.split('.');
    if (hostParts.length >= 2) {
      const rootDomain = '.' + hostParts.slice(-2).join('.');
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${rootDomain}`;
    }
  }

  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }

  function applyActive(lang) {
    items.forEach(li => li.classList.remove('active'));
    const activeLi = document.querySelector(`.multiLang ul li[lang="${lang}"]`);
    if (activeLi) activeLi.classList.add('active');
  }

  function switchLang(targetLang) {
    // Set translation cookie
    // For English: /sv/en
    // For Swedish: /sv/sv (or delete)
    const cookieValue = `/${SOURCE_LANG}/${targetLang}`;

    // If switching back to Swedish, you can either set /sv/sv or delete cookie.
    // Deleting is often cleaner:
    if (targetLang === SOURCE_LANG) {
      deleteCookieOnRootDomain('googtrans');
    } else {
      setCookieOnRootDomain('googtrans', cookieValue, 30);
    }

    // reload so GTranslate applies immediately
    location.reload();
  }
  const googtrans = getCookie('googtrans'); // e.g. "/sv/en"
  if (googtrans && googtrans.split('/').length >= 3) {
    const current = googtrans.split('/')[2] || SOURCE_LANG;
    applyActive(current);
  } else {
    applyActive(SOURCE_LANG);
  }

  // Click handlers
  items.forEach(function (li) {
    li.addEventListener('click', function () {
      items.forEach(x => x.classList.remove('active'));
      this.classList.add('active');

      const selectedLang = this.getAttribute('value'); // "sv" or "en"
      if (!selectedLang) return;

      switchLang(selectedLang);
    });
  });



});

document.addEventListener('DOMContentLoaded', function () {
  const header = document.querySelector('.header-section');
  if (!header) {
    console.log('header-section not found');
    return;
  }

  function handleHeaderScroll() {
    if (window.scrollY > 15) {
      header.classList.add('header-scrolled');
    } else {
      header.classList.remove('header-scrolled');
    }
  }

  handleHeaderScroll();
  window.addEventListener('scroll', handleHeaderScroll, { passive: true });
});